import { createFileRoute, useParams } from '@tanstack/react-router';
import * as React from 'react';
import {
  Archive,
  Download,
  Edit3,
  FileText,
  GitBranch,
  Link as LinkIcon,
  Snowflake,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { EmptyState } from '@/components/ui/empty-state';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { PdfPreviewModal } from '@/components/modals/pdf-preview-modal';
import { CaptureSnapshotModal } from '@/components/modals/capture-snapshot-modal';
import {
  useSnapshotDetailQuery,
  useSnapshotListQuery,
  type SnapshotDetail,
  type SnapshotListItem,
  type SnapshotType,
} from '@/lib/api/snapshots';
import { useEngagementQuery } from '@/lib/api/engagements';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import { useSnapshotMode, type SnapshotKind } from '@/lib/stores/snapshot-mode';
import { formatDateTime } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * Snapshot capture & viewer screen (T-0101 / E05-F01) — prototype parity
 * pass (C20-139).
 *
 * Layout follows `design_handoff_caliber_v2/prototype/screens-engagement.js
 * → Screens.snapshot` faithfully:
 *
 *   1. Title + subtitle ("Immutable, point-in-time captures · E05-F01 ·
 *      N on file · cross-module references frozen at capture").
 *   2. Header CTAs — "Snapshot history" (filters list to all),
 *      "Capture snapshot" (opens capture modal).
 *   3. "What is a snapshot?" explainer card — explains the 3-step
 *      workflow (author → capture → distribute). Always shown because
 *      consultants land here once a week and the 1-line story matters.
 *   4. KPI row — totals split by snapshot_type (Pulse / Readiness /
 *      Posture/Health). Numbers are computed from the live list so the
 *      hero is never stale.
 *   5. Two-pane layout — list on the left (380px fixed), preview on the
 *      right. Selecting a row loads the frozen state and enters
 *      snapshot mode (the global `<SnapshotBanner>` appears).
 *
 * Write-once contract (ADR-009): every UI affordance respects the
 * immutability. The detail card shows the frozen executive summary +
 * stats + risks read-only — no edit button, no overrides. "Regenerate
 * PDF" mints a NEW pdf_reports row (the existing snapshot row stays
 * untouched per ADR-009).
 *
 * Backend wire-up matrix (every element resolves to a real backend, or
 * surfaces a "Pending" message until the backend lands — never mock data):
 *   - List           → GET /api/v1/engagements/:id/snapshots
 *   - Type filter    → GET ?snapshot_type=...
 *   - KPI counts     → derived from the same list response
 *   - Selected row   → GET /api/v1/engagements/:id/snapshots/:snap_id
 *                       (frozen_state, source_ref_summary, captured_*)
 *   - Capture        → POST /api/v1/engagements/:id/snapshots
 *                       (idempotency-keyed)
 *   - PDF download   → GET /api/v1/engagements/:id/reports/:snap/pdf →
 *                       opens <PdfPreviewModal> (handles regenerate via
 *                       POST → mints a NEW pdf_reports row).
 *   - Share          → marked "Pending" — share-link tokenization is a
 *                       separate ticket; the button is rendered to
 *                       preserve the prototype's contract but is
 *                       disabled with a tooltip.
 *   - Snapshot mode  → enter/exit via `useSnapshotMode` so the global
 *                       `<SnapshotBanner>` self-shows.
 */

export const Route = createFileRoute('/engagements/$engagementId/snapshot')({
  component: SnapshotTab,
});

type SnapshotTypeFilter = 'all' | SnapshotType;

const SNAPSHOT_TYPE_LABEL: Record<SnapshotType, string> = {
  pulse: 'Pulse',
  posture: 'Health synthesis',
  readiness: 'Readiness',
  engagement: 'Engagement overview',
};

/**
 * Published reports land in the same snapshot store as the captures above
 * (ADR-009), so a detail's `snapshot_type` may be a report kind. The capture
 * kinds keep their labels; a report kind gets its own; anything newer reads
 * as its words.
 */
const REPORT_TYPE_LABEL: Record<string, string> = {
  vendor_intelligence_report: 'Vendor intelligence report',
  health_readiness_report: 'Health & readiness report',
  requirements_signoff: 'Requirements packet',
};

function snapshotTypeLabel(snapshotType: string): string {
  return (
    (SNAPSHOT_TYPE_LABEL as Record<string, string>)[snapshotType] ??
    REPORT_TYPE_LABEL[snapshotType] ??
    snapshotType.replace(/_/g, ' ')
  );
}

/**
 * Translate the API's `snapshot_type` to the {@link SnapshotKind}
 * vocabulary the snapshot-mode banner expects (`posture` → `health`).
 */
function toSnapshotKind(snapshotType: string): SnapshotKind {
  if (snapshotType === 'posture') return 'health';
  if (snapshotType === 'pulse') return 'pulse';
  if (snapshotType === 'readiness') return 'readiness';
  // Engagement-overview captures and published reports are consultant-initiated
  // freezes — the banner's `manual` kind.
  return 'manual';
}

function SnapshotTab() {
  const { engagementId } = useParams({ from: '/engagements/$engagementId/snapshot' });
  const engagementQuery = useEngagementQuery(engagementId);
  const [typeFilter, setTypeFilter] = React.useState<SnapshotTypeFilter>('all');
  const listOpts = typeFilter === 'all' ? {} : { snapshot_type: typeFilter };
  const list = useSnapshotListQuery(engagementId, listOpts);
  // Unfiltered list — used to drive the KPI row so the counts reflect
  // the engagement total, not the filter result.
  const totalsList = useSnapshotListQuery(engagementId, {});
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [capturing, setCapturing] = React.useState(false);
  const [pdfSnapshotId, setPdfSnapshotId] = React.useState<string | null>(null);

  const snapshots = list.data?.items ?? [];
  const allSnapshots = totalsList.data?.items ?? [];

  // KPI totals — keyed off the unfiltered list. Falls back to zero
  // while the query is still loading so the layout doesn't shift.
  const kpis = React.useMemo(() => {
    const pulse = allSnapshots.filter((s) => s.snapshot_type === 'pulse').length;
    const readiness = allSnapshots.filter((s) => s.snapshot_type === 'readiness').length;
    const posture = allSnapshots.filter((s) => s.snapshot_type === 'posture').length;
    const engagement = allSnapshots.filter((s) => s.snapshot_type === 'engagement').length;
    return { total: allSnapshots.length, pulse, readiness, posture, engagement };
  }, [allSnapshots]);

  // Auto-select the newest snapshot when none is chosen, so the
  // viewer renders something on first load (matches the prototype's
  // "active snapshot" default state).
  React.useEffect(() => {
    if (selectedId) return;
    if (snapshots.length > 0) setSelectedId(snapshots[0]!.id);
  }, [selectedId, snapshots]);

  const clientName = engagementQuery.data?.client_name ?? null;
  const engagementName = engagementQuery.data?.name ?? null;

  return (
    <div className="space-y-4">
      {/* ------------ Header ------------ */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Snapshots</h1>
          <p className="text-meta text-ink-500">
            Immutable, point-in-time captures · <span className="font-mono">{kpis.total}</span> on
            file · cross-module references frozen at capture
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setTypeFilter('all')}
            disabled={typeFilter === 'all'}
            title="Show all snapshot types"
          >
            <Archive className="h-4 w-4" aria-hidden="true" />
            Snapshot history
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setCapturing(true)}
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
          >
            <GitBranch className="h-4 w-4" aria-hidden="true" />
            Capture snapshot
          </Button>
        </div>
      </div>

      {/* ------------ Explainer card ------------ */}
      <ExplainerCard />

      {/* ------------ KPI row ------------ */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile
          label="Total snapshots"
          value={kpis.total}
          trend="across engagement to date"
          loading={totalsList.isLoading}
        />
        <KpiTile
          label="Engagement overviews"
          value={kpis.engagement}
          trend="captured from the overview"
          loading={totalsList.isLoading}
        />
        <KpiTile
          label="Pulse reports"
          value={kpis.pulse}
          trend="contract compliance"
          loading={totalsList.isLoading}
        />
        <KpiTile
          label="Readiness scorecards"
          value={kpis.readiness}
          trend="cutover decision artifacts"
          loading={totalsList.isLoading}
        />
        <KpiTile
          label="Health syntheses"
          value={kpis.posture}
          trend="health check"
          loading={totalsList.isLoading}
        />
      </div>

      {/* ------------ Two-pane: list / preview ------------ */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
        {/* ----- List card ----- */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center gap-2 border-b border-ink-100 px-4 py-3">
            <h2 className="flex-1 text-h3 font-semibold text-ink-900">All snapshots</h2>
            <Label htmlFor="snapshot-type-filter" className="sr-only">
              Filter snapshot type
            </Label>
            <select
              id="snapshot-type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as SnapshotTypeFilter)}
              className="h-7 rounded-card border border-ink-200 bg-paper-0 px-2 text-meta"
            >
              <option value="all">All types</option>
              <option value="engagement">Engagement overview</option>
              <option value="pulse">Pulse</option>
              <option value="readiness">Readiness</option>
              <option value="posture">Health synthesis</option>
            </select>
          </div>

          {list.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-body-sm text-ink-500">
              <Spinner /> Loading snapshots…
            </div>
          ) : list.error ? (
            <div className="p-4">
              <ErrorMessage error={list.error} />
            </div>
          ) : snapshots.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No snapshots yet"
                description="Capture a snapshot to freeze the current engagement state."
              />
            </div>
          ) : (
            <ul role="list" className="divide-y divide-ink-100">
              {snapshots.map((snap, i) => (
                <SnapshotListRow
                  key={snap.id}
                  snap={snap}
                  isLatest={typeFilter === 'all' && i === 0}
                  selected={selectedId === snap.id}
                  onSelect={() => setSelectedId(snap.id)}
                />
              ))}
            </ul>
          )}
        </Card>

        {/* ----- Preview pane ----- */}
        <div className="space-y-3">
          {selectedId ? (
            <SnapshotPreviewPane
              engagementId={engagementId}
              snapshotId={selectedId}
              clientName={clientName}
              engagementName={engagementName}
              onOpenPdf={() => setPdfSnapshotId(selectedId)}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-h3">
                  <Snowflake className="h-4 w-4 text-accent-amber-600" aria-hidden="true" />
                  Select a snapshot
                </CardTitle>
                <p className="text-meta text-ink-500">
                  Click a row on the left to view the frozen state. The snapshot banner appears at
                  the top of the page while you&apos;re viewing historical data.
                </p>
              </CardHeader>
              <CardContent>
                <p className="text-meta text-ink-500">
                  Snapshots preserve cross-module references resolved at capture time. The live
                  values today may differ — the snapshot preserves the originals.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {capturing ? (
        <CaptureSnapshotModal
          engagementId={engagementId}
          onClose={() => setCapturing(false)}
          onCaptured={(result) => {
            // Already on the Snapshots screen: close and select the new row
            // rather than showing the modal's "View in Snapshots" result.
            setCapturing(false);
            setSelectedId(result.id);
          }}
        />
      ) : null}

      {pdfSnapshotId ? (
        <PdfPreviewModal
          engagementId={engagementId}
          snapshotId={pdfSnapshotId}
          open={pdfSnapshotId !== null}
          onOpenChange={(next) => {
            if (!next) setPdfSnapshotId(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ============================================================================
// Explainer card
// ============================================================================

/**
 * Three-step explainer ("Author → Capture freezes → Distribute & archive")
 * mirroring the prototype's amber-tinted card. The copy is the consultant
 * onboarding story — kept on every visit because the page is rare-use.
 */
function ExplainerCard() {
  return (
    <section
      className="rounded-card border border-ink-100 p-4 shadow-sm"
      style={{
        borderLeft: '3px solid var(--accent-amber-600)',
        background: 'linear-gradient(135deg, #FFFCF6 0%, var(--paper-0) 60%)',
      }}
      aria-labelledby="snapshot-explainer-title"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-card bg-accent-amber-600 text-paper-0">
          <GitBranch className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="flex-1 space-y-3">
          <div>
            <h2 id="snapshot-explainer-title" className="text-h3 font-semibold text-ink-900">
              What is a snapshot?
            </h2>
            <p className="mt-1 text-body-sm text-ink-700">
              A snapshot is an{' '}
              <strong className="font-semibold">immutable, point-in-time capture</strong> of an
              engagement deliverable — an engagement overview, a pulse report, a Health Check
              synthesis, or a Go-Live readiness scorecard. At capture, every cross-module reference
              (a finding cited in a deviation, a deliverable status cited in a scorecard) is{' '}
              <strong className="font-semibold">resolved and frozen inline</strong>. Re-opening a
              snapshot 6 months later shows exactly what was distributed, even if the underlying
              registers have moved on.
            </p>
          </div>
          <ol className="grid gap-2 sm:grid-cols-3">
            <ExplainerStep n={1} icon={<Edit3 className="h-3 w-3" aria-hidden="true" />}>
              <strong className="font-semibold text-ink-900">Author the deliverable.</strong>{' '}
              Consultant edits the AI-drafted pulse / synthesis / scorecard until ready to publish.
            </ExplainerStep>
            <ExplainerStep n={2} icon={<Snowflake className="h-3 w-3" aria-hidden="true" />}>
              <strong className="font-semibold text-ink-900">Capture freezes the state.</strong>{' '}
              Click <em>Capture snapshot</em>. Cross-module references resolve inline and the row
              becomes read-only.
            </ExplainerStep>
            <ExplainerStep n={3} icon={<Download className="h-3 w-3" aria-hidden="true" />}>
              <strong className="font-semibold text-ink-900">Distribute &amp; archive.</strong> PDF
              is rendered through the firm brand template and the snapshot is included in the
              engagement archive.
            </ExplainerStep>
          </ol>
          <p className="text-meta text-ink-500">
            Snapshots are captured four ways today:{' '}
            <strong className="font-semibold text-ink-700">
              (a) &ldquo;Capture snapshot&rdquo;
            </strong>{' '}
            on the engagement overview or this page,{' '}
            <strong className="font-semibold text-ink-700">(b) capturing a scorecard</strong> from
            Go-Live Readiness,{' '}
            <strong className="font-semibold text-ink-700">(c) publishing a pulse</strong> from
            Contract Compliance, and{' '}
            <strong className="font-semibold text-ink-700">(d) publishing a synthesis</strong> from
            Health Check. Every capture is started by a person; scheduled and gate-triggered
            captures are not available yet. Snapshots are never edited or deleted; they&apos;re
            queryable for the audit life of the engagement.
          </p>
        </div>
      </div>
    </section>
  );
}

function ExplainerStep({
  n,
  icon,
  children,
}: {
  n: number;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li className="rounded-card border border-ink-100 bg-paper-0 px-3 py-2.5">
      <div className="flex items-center gap-1.5">
        <span className="flex h-5 w-5 items-center justify-center rounded-card bg-accent-amber-100 text-accent-amber-700">
          {icon}
        </span>
        <span className="text-mono-sm font-mono text-ink-500">Step {n}</span>
      </div>
      <p className="mt-1.5 text-meta leading-[16px] text-ink-700">{children}</p>
    </li>
  );
}

// ============================================================================
// KPI tile
// ============================================================================

function KpiTile({
  label,
  value,
  trend,
  loading,
}: {
  label: string;
  value: number;
  trend: string;
  loading?: boolean;
}) {
  return (
    <div className="rounded-card border border-ink-100 bg-paper-0 px-3 py-3 shadow-sm">
      <div className="text-meta text-ink-500">{label}</div>
      <div className="mt-1 font-serif text-h1 font-semibold leading-none text-ink-900 tabular-nums">
        {loading ? <Spinner /> : value}
      </div>
      <div className="mt-1 text-meta text-ink-500">{trend}</div>
    </div>
  );
}

// ============================================================================
// List row
// ============================================================================

function SnapshotListRow({
  snap,
  isLatest,
  selected,
  onSelect,
}: {
  snap: SnapshotListItem;
  isLatest: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const kindLabel = SNAPSHOT_TYPE_LABEL[snap.snapshot_type];
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={cn(
          'block w-full px-4 py-3 text-left transition-colors hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
          selected && 'bg-paper-50 shadow-[inset_2px_0_0_var(--accent-amber-600)]',
        )}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex h-[18px] items-center rounded-full bg-ink-100 px-2 text-[10px] font-medium text-ink-700">
            {kindLabel}
          </span>
          {isLatest ? (
            <span className="inline-flex h-[18px] items-center rounded-full bg-accent-amber-100 px-2 text-[10px] font-medium text-accent-amber-700">
              Latest
            </span>
          ) : null}
          <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10.5px] text-ink-500">
            <Snowflake className="h-3 w-3" aria-hidden="true" />
            immutable
          </span>
        </div>
        <div className="mt-1.5 text-body-sm font-medium text-ink-900">
          {kindLabel} · seq <span className="font-mono">{snap.sequence_no}</span>
        </div>
        <div className="mt-0.5 text-meta text-ink-500">{formatDateTime(snap.captured_at)}</div>
        <div className="mt-0.5 font-mono text-mono-sm text-ink-300">{snap.id.slice(0, 8)}…</div>
      </button>
    </li>
  );
}

// ============================================================================
// Preview pane (right column)
// ============================================================================

function SnapshotPreviewPane({
  engagementId,
  snapshotId,
  clientName,
  engagementName,
  onOpenPdf,
}: {
  engagementId: string;
  snapshotId: string;
  clientName: string | null;
  engagementName: string | null;
  onOpenPdf: () => void;
}) {
  const detail = useSnapshotDetailQuery(engagementId, snapshotId);
  const enterSnapshotMode = useSnapshotMode((s) => s.enterSnapshotMode);
  const exitSnapshotMode = useSnapshotMode((s) => s.exitSnapshotMode);

  // `captured_by` is a user id. Resolve it to a display name for the
  // banner + metadata card; fall back to the id only if the lookup fails
  // or the user is no longer resolvable.
  const capturedById = detail.data?.captured_by ?? null;
  const capturedByLookup = useUsersLookupQuery(capturedById ? [capturedById] : []);
  const capturedByName =
    capturedByLookup.data?.items.find((u) => u.id === capturedById)?.display_name ?? null;
  const capturedByLabel = capturedByName ?? capturedById ?? '';
  const capturedBySettled = !capturedById || !capturedByLookup.isLoading;

  // Enter snapshot mode when the detail loads; exit on unmount or
  // snapshot change. ADR-009 — the SnapshotBanner is the user-visible
  // contract that the view is read-only. Waits for the name lookup to
  // settle so the banner never flashes a raw user id.
  React.useEffect(() => {
    if (!detail.data || !capturedBySettled) return;
    enterSnapshotMode({
      snapshot_id: detail.data.id,
      captured_at: detail.data.captured_at,
      captured_by: capturedByLabel,
      kind: toSnapshotKind(detail.data.snapshot_type),
    });
    return () => {
      exitSnapshotMode();
    };
  }, [detail.data, capturedBySettled, capturedByLabel, enterSnapshotMode, exitSnapshotMode]);

  if (detail.isLoading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-body-sm text-ink-500">
            <Spinner /> Loading snapshot…
          </div>
        </CardContent>
      </Card>
    );
  }
  if (detail.error) {
    return (
      <Card>
        <CardContent className="p-4">
          <ErrorMessage error={detail.error} />
        </CardContent>
      </Card>
    );
  }
  if (!detail.data) return null;

  const snapshot = detail.data;

  return (
    <div className="space-y-3">
      {/* Active snapshot banner — local to the preview pane. The global
          banner sits at the top of the viewport; this one identifies the
          *selected* snapshot inside the page surface, with PDF + Share. */}
      <ActiveSnapshotBanner snapshot={snapshot} onOpenPdf={onOpenPdf} />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
        <FrozenDeliverableCard
          snapshot={snapshot}
          clientName={clientName}
          engagementName={engagementName}
        />
        <SnapshotMetadataCard snapshot={snapshot} capturedByName={capturedByName} />
      </div>
    </div>
  );
}

// ----- Active snapshot banner inside the preview pane -----
function ActiveSnapshotBanner({
  snapshot,
  onOpenPdf,
}: {
  snapshot: SnapshotDetail;
  onOpenPdf: () => void;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 rounded-card border border-ink-100 bg-paper-100 px-3 py-2 text-meta text-ink-700"
      data-snapshot-id={snapshot.id}
    >
      <span className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-paper-0 px-2 py-0.5 text-[10px] font-medium text-ink-500">
        <GitBranch className="h-3 w-3" aria-hidden="true" />
        Viewing snapshot
      </span>
      <span className="font-medium text-ink-700">
        {snapshotTypeLabel(snapshot.snapshot_type)} · seq{' '}
        <span className="font-mono">{snapshot.sequence_no}</span>
      </span>
      <span className="text-ink-500">
        captured <span className="font-mono">{formatDateTime(snapshot.captured_at)}</span> · cross-
        module data frozen at this moment
      </span>
      <span className="ml-auto flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onOpenPdf}
          className="h-7 px-2"
          title="Open PDF preview"
        >
          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
          PDF
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled
          className="h-7 px-2"
          title="Share-link tokenization is pending (separate ticket)"
        >
          <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
          Share
        </Button>
      </span>
    </div>
  );
}

// ----- Frozen deliverable card -----
//
// Reads the `frozen_state` JSON and surfaces well-known fields. The shape
// is soft per T-0057 (collator-dependent), so we render each section
// defensively: if a field is missing, we render the "Pending" placeholder
// instead of synthesising a value. The full payload remains reachable
// via the metadata card's "Open as JSON" reveal so consultants can drill
// in when needed.
function FrozenDeliverableCard({
  snapshot,
  clientName,
  engagementName,
}: {
  snapshot: SnapshotDetail;
  clientName: string | null;
  engagementName: string | null;
}) {
  const fz = (snapshot.frozen_state ?? {}) as Record<string, unknown>;
  const summary = pickString(fz, ['executive_summary', 'summary', 'narrative', 'executive']);
  const overviewSlice = pickEngagementOverview(fz);
  const posture = overviewSlice?.posture ?? pickPostureStatus(fz);
  const periodLabel = pickString(fz, [
    'period_label',
    'reporting_period',
    'pulse_period',
    'period',
  ]);
  const stats = overviewSlice?.stats ?? pickStats(fz);
  const risks = pickRisks(fz);
  const refSummary = (snapshot.source_ref_summary ?? null) as Record<string, unknown> | null;
  const refCount = countSourceRefs(refSummary);
  const title = `${snapshotTypeLabel(snapshot.snapshot_type)} report`;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-h2 font-semibold text-ink-900">{title}</CardTitle>
            <p className="mt-1 text-meta text-ink-500">
              {overviewSlice
                ? `As captured${overviewSlice.currentPhase ? ` · ${overviewSlice.currentPhase} phase` : ''}`
                : (periodLabel ?? 'No reporting period recorded')}
              {clientName ? <> · {clientName}</> : null}
              {engagementName ? <> · {engagementName}</> : null}
            </p>
          </div>
          {posture ? <PostureChip status={posture} /> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Executive summary — narrative snapshots only. Engagement-overview
            snapshots carry no narrative (numbers and status only), so the
            section is omitted rather than shown as an empty placeholder. */}
        {overviewSlice ? null : (
          <section>
            <h3 className="text-h3 font-semibold text-ink-900">Executive summary</h3>
            {summary ? (
              <p className="mt-1.5 whitespace-pre-line rounded-card border border-ink-100 bg-paper-50 px-4 py-3 text-body-sm leading-[22px] text-ink-700">
                {summary}
              </p>
            ) : (
              <PendingNote>
                This snapshot has no executive summary. Summaries are included when a pulse or
                health synthesis is published from its own module.
              </PendingNote>
            )}
          </section>
        )}

        {/* Frozen stats */}
        <section>
          <h3 className="text-h3 font-semibold text-ink-900">Frozen stats</h3>
          {stats.length > 0 ? (
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {stats.map((s, i) => (
                <FrozenStat key={`${s.label}-${i}`} {...s} />
              ))}
            </div>
          ) : (
            <PendingNote>
              This snapshot has no headline stats. Capture an Engagement overview snapshot to freeze
              go-live date, readiness, open issues and overdue deliverables.
            </PendingNote>
          )}
        </section>

        {overviewSlice ? (
          <>
            {/* Health Check posture by domain */}
            <section>
              <h3 className="text-h3 font-semibold text-ink-900">Health Check posture</h3>
              {overviewSlice.health.state === 'off' ? (
                <PendingNote>Health Check was not enabled at capture.</PendingNote>
              ) : overviewSlice.health.domains.length === 0 ? (
                <PendingNote>No active health domains at capture.</PendingNote>
              ) : (
                <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {overviewSlice.health.domains.map((d, i) => (
                    <li
                      key={`${d.name}-${i}`}
                      className="flex items-center justify-between gap-2 rounded-card border border-ink-100 bg-paper-0 px-3 py-2"
                    >
                      <span className="truncate text-body-sm text-ink-900">{d.name}</span>
                      <PostureChip status={d.posture} />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Readiness + deliverables — one line each, values as frozen */}
            <section>
              <h3 className="text-h3 font-semibold text-ink-900">Go-Live readiness</h3>
              {overviewSlice.readiness.state === 'off' ? (
                <PendingNote>Go-Live Readiness was not enabled at capture.</PendingNote>
              ) : overviewSlice.readiness.state === 'unavailable' ? (
                <PendingNote>No scorecard was available at capture.</PendingNote>
              ) : (
                <p className="mt-1.5 text-body-sm leading-[20px] text-ink-700">
                  Weighted total{' '}
                  <span className="font-mono font-semibold text-ink-900">
                    {overviewSlice.readiness.total}
                  </span>
                  {overviewSlice.readiness.recommendation ? (
                    <>
                      {' '}
                      · recommendation {overviewSlice.readiness.recommendation.replace(/_/g, ' ')}
                    </>
                  ) : null}{' '}
                  · <span className="font-mono">{overviewSlice.readiness.scored}</span> of{' '}
                  <span className="font-mono">{overviewSlice.readiness.criteria}</span> criteria
                  scored
                </p>
              )}
            </section>
            <section>
              <h3 className="text-h3 font-semibold text-ink-900">Contract deliverables</h3>
              {overviewSlice.deliverables.state === 'off' ? (
                <PendingNote>Contract Compliance was not enabled at capture.</PendingNote>
              ) : (
                <p className="mt-1.5 text-body-sm leading-[20px] text-ink-700">
                  <span className="font-mono">{overviewSlice.deliverables.total}</span> on the
                  register ·{' '}
                  <span className="font-mono text-posture-critical">
                    {overviewSlice.deliverables.overdue}
                  </span>{' '}
                  overdue
                </p>
              )}
            </section>

            {/* Recent signals */}
            <section>
              <h3 className="text-h3 font-semibold text-ink-900">Recent signals</h3>
              {overviewSlice.signals.length === 0 ? (
                <PendingNote>No recent signals at capture.</PendingNote>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {overviewSlice.signals.map((sig, i) => (
                    <li key={i} className="flex items-baseline gap-3 text-body-sm text-ink-700">
                      <span className="w-[150px] flex-none font-mono text-mono-sm text-ink-500">
                        {sig.when ? formatDateTime(sig.when) : '—'}
                      </span>
                      <span>{sig.summary}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : (
          // Top risks — narrative snapshots only.
          <section>
            <h3 className="text-h3 font-semibold text-ink-900">Top risks this period</h3>
            {risks.length > 0 ? (
              <ul className="mt-2 space-y-2">
                {risks.map((r, i) => (
                  <FrozenRiskRow key={`${r.id}-${i}`} {...r} />
                ))}
              </ul>
            ) : (
              <PendingNote>
                This snapshot has no pinned risks. Top risks are included when a pulse is published
                from Contract Compliance.
              </PendingNote>
            )}
          </section>
        )}

        {/* Cross-module references */}
        <section>
          <h3 className="text-h3 font-semibold text-ink-900">Cross-module references</h3>
          {refCount > 0 ? (
            <p className="mt-1.5 text-body-sm leading-[20px] text-ink-700">
              <span className="font-mono">{refCount}</span> cross-module reference
              {refCount === 1 ? '' : 's'} were resolved at capture time. The live values today may
              differ — the snapshot preserves the originals.
              <span className="ml-1 inline-flex h-[18px] items-center rounded-full bg-ink-100 px-2 text-[10px] text-ink-300">
                frozen
              </span>
            </p>
          ) : (
            <PendingNote>No cross-module references were frozen with this snapshot.</PendingNote>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

// ----- Side metadata card -----
function SnapshotMetadataCard({
  snapshot,
  capturedByName,
}: {
  snapshot: SnapshotDetail;
  /** Resolved display name for `snapshot.captured_by`; null while unknown. */
  capturedByName: string | null;
}) {
  const [showJson, setShowJson] = React.useState(false);
  const refCount = countSourceRefs(snapshot.source_ref_summary);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-h3 font-semibold text-ink-900">Snapshot metadata</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <MetadataRow label="Snapshot ID">
          <span className="font-mono text-mono-sm text-ink-700">{snapshot.id}</span>
        </MetadataRow>
        <MetadataRow label="Sequence">
          <span className="font-mono tabular-nums">{snapshot.sequence_no}</span>
        </MetadataRow>
        <MetadataRow label="Captured by">
          {capturedByName ? (
            <span title={snapshot.captured_by}>{capturedByName}</span>
          ) : (
            <span className="font-mono text-mono-sm">{snapshot.captured_by}</span>
          )}
        </MetadataRow>
        <MetadataRow label="Captured at">
          <span className="font-mono tabular-nums">{formatDateTime(snapshot.captured_at)}</span>
        </MetadataRow>
        <MetadataRow label="Frozen refs">
          <span>
            <span className="font-mono">{refCount}</span> cross-module · resolved at capture
          </span>
        </MetadataRow>
        <MetadataRow label="Size">
          <span className="font-mono tabular-nums">
            {(snapshot.size_bytes / 1024).toFixed(1)} KB
          </span>
        </MetadataRow>
        <MetadataRow label="Action surfaces">
          <span className="inline-flex h-[22px] items-center gap-1.5 rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-500">
            <Snowflake className="h-3 w-3" aria-hidden="true" />
            Read-only
          </span>
        </MetadataRow>
        <hr className="border-ink-100" />
        <button
          type="button"
          onClick={() => setShowJson((p) => !p)}
          className="text-meta font-medium text-accent-amber-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          {showJson ? 'Hide raw frozen_state' : 'Open as JSON'}
        </button>
        {showJson ? (
          <pre className="max-h-72 overflow-auto rounded-card border border-ink-100 bg-paper-50 p-3 font-mono text-mono-sm text-ink-700">
            {formatJson({
              frozen_state: snapshot.frozen_state,
              source_ref_summary: snapshot.source_ref_summary,
            })}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MetadataRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-meta text-ink-500">{label}</span>
      <span className="text-body-sm text-ink-900">{children}</span>
    </div>
  );
}

function PendingNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-1.5 rounded-card border border-dashed border-ink-200 bg-paper-50 px-3 py-2 text-meta text-ink-500">
      <span className="mr-1 inline-flex h-[18px] items-center rounded-full bg-ink-100 px-2 text-[10px] font-medium text-ink-500">
        Pending
      </span>
      {children}
    </p>
  );
}

function FrozenStat({
  label,
  value,
  posture,
}: {
  label: string;
  value: string;
  posture: PostureStatus | null;
}) {
  const borderColor = posture
    ? `var(--posture-${posture === 'at-risk' ? 'at-risk' : posture})`
    : 'var(--ink-200)';
  return (
    <div
      className="rounded-card border border-ink-100 bg-paper-0 px-3 py-2.5"
      style={{ borderLeft: `3px solid ${borderColor}` }}
    >
      <div className="text-meta text-ink-500">{label}</div>
      <div className="mt-1 text-h2 font-semibold tabular-nums text-ink-900">{value}</div>
    </div>
  );
}

function FrozenRiskRow({
  id,
  text,
  severity,
}: {
  id: string;
  text: string;
  severity: 'high' | 'medium' | 'low' | 'unassessed';
}) {
  const severityChipClass =
    severity === 'high'
      ? 'bg-posture-critical-tint text-posture-critical'
      : severity === 'medium'
        ? 'bg-posture-at-risk-tint text-posture-at-risk'
        : severity === 'low'
          ? 'bg-posture-healthy-tint text-posture-healthy'
          : 'bg-ink-100 text-posture-unassessed';
  return (
    <li className="flex items-start gap-3">
      <span
        className={cn(
          'inline-flex h-5 items-center rounded-full px-2 font-mono text-[11px] font-semibold',
          severityChipClass,
        )}
      >
        {id}
      </span>
      <span className="flex-1 text-body-sm leading-[20px] text-ink-700">{text}</span>
      <span className="inline-flex h-5 items-center rounded-full bg-ink-100 px-2 text-[10px] text-ink-300">
        frozen
      </span>
    </li>
  );
}

// ============================================================================
// frozen_state shape helpers
// ============================================================================
//
// `frozen_state` is collator-driven per T-0057 so the shape varies by
// snapshot_type. These helpers consume the top-level keys we know about
// today and return strict, typed slices the cards can render. Each
// helper is intentionally tolerant: missing keys return null / [] so the
// caller renders the "Pending" placeholder rather than crashing.

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return null;
}

function pickPostureStatus(obj: Record<string, unknown>): PostureStatus | null {
  for (const key of ['posture', 'overall_posture', 'status']) {
    const v = obj[key];
    if (typeof v !== 'string') continue;
    const norm = v.toLowerCase().replace(/_/g, '-');
    if (norm === 'healthy' || norm === 'at-risk' || norm === 'critical' || norm === 'unassessed') {
      return norm;
    }
  }
  return null;
}

interface FrozenStatT {
  label: string;
  value: string;
  posture: PostureStatus | null;
}

function pickStats(obj: Record<string, unknown>): FrozenStatT[] {
  const arr = obj['stats'] ?? obj['kpis'] ?? obj['summary_stats'];
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
    .slice(0, 4)
    .map((row) => {
      const label = typeof row.label === 'string' ? row.label : 'Unlabeled';
      const value =
        typeof row.value === 'string'
          ? row.value
          : typeof row.value === 'number'
            ? String(row.value)
            : '—';
      const posture = pickPostureStatus(row);
      return { label, value, posture };
    });
}

interface EngagementOverviewSlice {
  posture: PostureStatus | null;
  stats: FrozenStatT[];
  currentPhase: string | null;
  health: {
    state: 'off' | 'unavailable' | 'present';
    domains: Array<{ name: string; posture: PostureStatus }>;
  };
  readiness: {
    state: 'off' | 'unavailable' | 'present';
    recommendation: string | null;
    scored: string;
    criteria: string;
    total: string;
  };
  deliverables: { state: 'off' | 'unavailable' | 'present'; total: string; overdue: string };
  signals: Array<{ when: string | null; summary: string }>;
}

/**
 * Engagement-overview snapshots (`snapshot_kind='engagement'`) keep their
 * picture under `frozen_state.overview` (see apps/api
 * services/engagement-snapshot.ts). Project it onto the card's posture +
 * four headline stats. Values are printed as frozen — nothing is
 * recomputed here. Returns null for every other snapshot kind.
 */
function pickEngagementOverview(obj: Record<string, unknown>): EngagementOverviewSlice | null {
  if (obj['snapshot_kind'] !== 'engagement') return null;
  const overview = obj['overview'];
  if (overview === null || typeof overview !== 'object') return null;
  const ov = overview as Record<string, unknown>;
  const asObj = (v: unknown): Record<string, unknown> =>
    v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  const facts = asObj(ov['facts']);
  const health = asObj(ov['health_posture']);
  const readiness = asObj(ov['readiness_scorecard']);

  const ROLLUP_TO_POSTURE: Record<string, PostureStatus> = {
    green: 'healthy',
    yellow_divergent: 'at-risk',
    yellow: 'at-risk',
    red: 'critical',
    grey: 'unassessed',
  };
  const rollup = health['overall_rollup'];
  const posture = typeof rollup === 'string' ? (ROLLUP_TO_POSTURE[rollup] ?? null) : null;

  const num = (v: unknown): string => (typeof v === 'number' ? String(v) : '—');
  const overdue = facts['overdue_count'];
  const total = readiness['total_percent'];

  // A module section is: null (module off), { unavailable } (on, nothing
  // to show yet), or the real frozen object.
  const sectionState = (v: unknown): 'off' | 'unavailable' | 'present' =>
    v === null || v === undefined
      ? 'off'
      : typeof asObj(v)['unavailable'] === 'string'
        ? 'unavailable'
        : 'present';

  const domains = (Array.isArray(health['per_domain']) ? health['per_domain'] : [])
    .map(asObj)
    .map((d) => ({
      name: typeof d['name'] === 'string' ? (d['name'] as string) : 'Unnamed domain',
      posture:
        typeof d['latest_rollup_color'] === 'string'
          ? (ROLLUP_TO_POSTURE[d['latest_rollup_color'] as string] ?? 'unassessed')
          : ('unassessed' as PostureStatus),
    }));

  const deliverablesRaw = ov['deliverables'];
  const deliverables = asObj(deliverablesRaw);
  const signals = (Array.isArray(ov['recent_signals']) ? ov['recent_signals'] : [])
    .map(asObj)
    .slice(0, 5)
    .map((sig) => ({
      when: typeof sig['occurred_at'] === 'string' ? (sig['occurred_at'] as string) : null,
      summary: typeof sig['summary'] === 'string' ? (sig['summary'] as string) : 'Signal',
    }));

  return {
    currentPhase:
      typeof facts['current_phase'] === 'string' ? (facts['current_phase'] as string) : null,
    health: { state: sectionState(ov['health_posture']), domains },
    readiness: {
      state: sectionState(ov['readiness_scorecard']),
      recommendation:
        typeof readiness['recommendation'] === 'string'
          ? (readiness['recommendation'] as string)
          : null,
      scored: num(readiness['scored_count']),
      criteria: num(readiness['criteria_count']),
      total: typeof total === 'number' ? `${total}%` : '—',
    },
    deliverables: {
      state: sectionState(deliverablesRaw),
      total: num(deliverables['total']),
      overdue: num(deliverables['overdue']),
    },
    signals,
    posture,
    stats: [
      {
        label: 'Planned go-live',
        value: typeof facts['planned_end'] === 'string' ? (facts['planned_end'] as string) : '—',
        posture: null,
      },
      {
        label: 'Readiness',
        value: typeof total === 'number' ? `${total}%` : '—',
        posture: null,
      },
      { label: 'Open issues', value: num(facts['open_issues_count']), posture: null },
      {
        label: 'Overdue deliverables',
        value: num(overdue),
        posture: typeof overdue === 'number' && overdue > 0 ? 'critical' : null,
      },
    ],
  };
}

interface FrozenRiskT {
  id: string;
  text: string;
  severity: 'high' | 'medium' | 'low' | 'unassessed';
}

function pickRisks(obj: Record<string, unknown>): FrozenRiskT[] {
  const arr = obj['top_risks'] ?? obj['risks'] ?? obj['highlighted_risks'];
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
    .slice(0, 5)
    .map((row) => {
      const id =
        typeof row.id === 'string' ? row.id : typeof row.code === 'string' ? row.code : 'R-?';
      const text =
        typeof row.text === 'string'
          ? row.text
          : typeof row.title === 'string'
            ? row.title
            : typeof row.summary === 'string'
              ? row.summary
              : 'Untitled risk';
      const severityRaw =
        typeof row.severity === 'string'
          ? row.severity.toLowerCase()
          : typeof row.priority === 'string'
            ? row.priority.toLowerCase()
            : '';
      const severity: FrozenRiskT['severity'] =
        severityRaw === 'high' || severityRaw === 'critical'
          ? 'high'
          : severityRaw === 'medium' || severityRaw === 'med' || severityRaw === 'moderate'
            ? 'medium'
            : severityRaw === 'low'
              ? 'low'
              : 'unassessed';
      return { id, text, severity };
    });
}

function countSourceRefs(refSummary: unknown): number {
  if (!refSummary || typeof refSummary !== 'object') return 0;
  // The T-0057 service emits source_ref_summary as `{ resolved_refs: [...], ... }`.
  // Fall back to a top-level array sum if the shape evolves.
  const obj = refSummary as Record<string, unknown>;
  const resolved = obj['resolved_refs'];
  if (Array.isArray(resolved)) return resolved.length;
  let total = 0;
  for (const v of Object.values(obj)) {
    if (Array.isArray(v)) total += v.length;
  }
  return total;
}

function formatJson(v: unknown): string {
  if (v === null || v === undefined) return '— null —';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export { SnapshotTab };
